import { EXCLUSION_LABEL, LOCATION_LABEL, fmt, type CalcResult, type Equipment } from "@h2lca/core";
import { IconButton } from "@/components/ui/Drawer";
import { templateById, useMaster } from "@/store/master";
import { useWorkshop } from "@/store/workshop";
import { StageBadge } from "./Badges";
import { EquipmentIcon } from "./EquipmentIcon";

/**
 * 공정 흐름도 , 순서대로 늘어선 설비 카드. 시스템 경계 안 설비는 주황 점선 상자 안에, 밖 설비는 흐리게.
 * 카드를 누르면 선택, 화살표로 순서 이동, ✕ 로 제거.
 */
export function FlowDiagram({ equipment, result, editable = true }: { equipment: Equipment[]; result: CalcResult; editable?: boolean }) {
  const ordered = [...equipment].sort((a, b) => a.order - b.order);
  const inB = (e: Equipment) => result.byEquipment.find((r) => r.equipmentId === e.id)?.inBoundary ?? e.inBoundary;

  // 연속된 경계 안 설비를 한 상자로 묶는다
  const runs: { inBoundary: boolean; items: Equipment[] }[] = [];
  for (const e of ordered) {
    const b = inB(e);
    const last = runs[runs.length - 1];
    if (last && last.inBoundary === b) last.items.push(e);
    else runs.push({ inBoundary: b, items: [e] });
  }

  if (ordered.length === 0) {
    return (
      <div className="flow-canvas">
        <div className="flow-empty">
          <EquipmentIcon icon="box" className="size-icon-empty text-ink-subtle" />
          <p>아직 설비가 없습니다.</p>
          <p className="text-footnote">설비 팔레트에서 설비를 선택해 추가하세요.</p>
        </div>
      </div>
    );
  }

  let seq = 0;
  return (
    <div className="flow-canvas">
      <div className="flow-row">
        {runs.map((run, ri) => {
          const cards = run.items.map((e, i) => {
            const idx = seq++;
            const prev = ordered[idx - 1];
            return (
              <div key={e.id} className="flex items-stretch gap-item">
                {(i > 0 || ri > 0) && <Arrow kg={prev && prev.h2.role !== "none" ? prev.h2.outKg : null} />}
                <FlowCard eq={e} result={result} editable={editable} first={idx === 0} last={idx === ordered.length - 1} />
              </div>
            );
          });
          return run.inBoundary ? (
            <div key={`run-${ri}`} className="flow-boundary">
              <span className="flow-boundary-label">시스템 경계</span>
              {cards}
            </div>
          ) : (
            <div key={`run-${ri}`} className="flex flex-wrap items-stretch gap-item p-label">
              {cards}
            </div>
          );
        })}
      </div>
      <p className="mt-item text-footnote text-ink-subtle">화살표 위 숫자는 앞 설비에서 나가는 수소량(kg). 점선 상자 밖 설비는 계산에서 제외됩니다.</p>
    </div>
  );
}

function Arrow({ kg }: { kg: number | null }) {
  return (
    <div className="flow-arrow" aria-hidden>
      {kg !== null && <span className="font-mono">{fmt(kg)} kg</span>}
      <svg viewBox="0 0 24 24" className="size-icon-nav" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 12h18M14 5l7 7-7 7" />
      </svg>
    </div>
  );
}

function FlowCard({ eq, result, editable, first, last }: { eq: Equipment; result: CalcResult; editable: boolean; first: boolean; last: boolean }) {
  const data = useMaster((s) => s.data);
  const selected = useWorkshop((s) => s.selectedEquipmentId) === eq.id;
  const select = useWorkshop((s) => s.select);
  const move = useWorkshop((s) => s.move);
  const remove = useWorkshop((s) => s.removeEquipment);
  const r = result.byEquipment.find((x) => x.equipmentId === eq.id);
  const out = r ? !r.inBoundary : !eq.inBoundary;
  const icon = templateById(data, eq.templateId)?.icon ?? "box";
  const preview = eq.inputs.filter((i) => i.kind !== "parameter").slice(0, 2);
  const reason = r?.forcedOut ? "인증제 산정범위 밖" : eq.exclusionReason ? EXCLUSION_LABEL[eq.exclusionReason] : "산정 제외";

  return (
    <div className="flow-card" data-selected={selected} data-out={out}>
      <button type="button" className="flex items-start gap-inline text-left" onClick={() => select(eq.id)} aria-pressed={selected}>
        <span className="mt-detail shrink-0 text-ink-soft">
          <EquipmentIcon icon={icon} className="size-icon" />
        </span>
        <span className="min-w-0">
          <span className="flow-card-name block">{eq.name}</span>
          <span className="mt-detail flex flex-wrap items-center gap-label">
            <StageBadge stage={eq.stage} />
            <span className="text-micro text-ink-subtle">{LOCATION_LABEL[eq.location]}</span>
          </span>
        </span>
      </button>
      <div className="flow-card-rows">
        {preview.map((i) => (
          <span key={i.id} className="flow-card-row">
            <span className="truncate">{i.label}</span>
            <b>
              {i.amount === null ? "없음" : fmt(i.amount)} <span className="font-normal text-ink-subtle">{i.unit}</span>
            </b>
          </span>
        ))}
        {eq.inputs.length === 0 && <span className="text-ink-subtle">입력 항목 없음</span>}
        {eq.h2.role !== "none" && (
          <span className="flow-card-row">
            <span>수소 {eq.h2.role === "producer" ? "생산" : "산출"}</span>
            <b>
              {eq.h2.outKg === null ? "없음" : fmt(eq.h2.outKg)} <span className="font-normal text-ink-subtle">kg</span>
            </b>
          </span>
        )}
        {r && r.inBoundary && (
          <span className="flow-card-row">
            <span>배출량</span>
            <b>
              {fmt(r.kgCO2eq)} <span className="font-normal text-ink-subtle">kgCO2eq</span>
            </b>
          </span>
        )}
      </div>
      {out && <span className="badge-soft badge-boundary self-start">경계 밖, {reason}</span>}
      {editable && (
        <div className="flow-card-actions">
          <IconButton label="앞으로" onClick={() => move(eq.id, -1)} className={first ? "invisible" : ""}>
            <svg viewBox="0 0 20 20" className="size-icon-action" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
              <path d="M12 4l-6 6 6 6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </IconButton>
          <IconButton label="뒤로" onClick={() => move(eq.id, 1)} className={last ? "invisible" : ""}>
            <svg viewBox="0 0 20 20" className="size-icon-action" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
              <path d="M8 4l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </IconButton>
          <span className="ml-auto">
            <IconButton label="설비 제거" onClick={() => remove(eq.id)}>
              <svg viewBox="0 0 20 20" className="size-icon-action" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
                <path d="M5 5l10 10M15 5L5 15" strokeLinecap="round" />
              </svg>
            </IconButton>
          </span>
        </div>
      )}
    </div>
  );
}
